/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Card, Form, Input, Select, DatePicker, Checkbox, message, Row, Col, Spin, Upload, InputNumber, Avatar, Modal as AntModal, Tag } from 'antd';
import { Button, ActionMenu, Modal } from '../../design-system';
import { BUTTON_PRESETS } from '../../design-system/constants';
import { ArrowLeftOutlined, UploadOutlined, UserOutlined, EditOutlined } from '@ant-design/icons';
import { userService } from '../../services/userService';
import dayjs from 'dayjs';
import { getApiErrorMessage } from '../../utils/errorUtils';
import roleService, { ROLES } from '../../service/RoleService';
import { normalizeToCode } from '../../utils/enumNormalizer';
import { getAutoProbationEndDate, requiresProbationEndDate } from './userEditValidation';
import { getEditableRoles, normalizeRoles, requiresAllOfficeAccess } from './userEditRoles';

const { TextArea } = Input;
const EXIT_FEEDBACK_TEST_EMAIL = 'test@mediatropy.com';

const renderGroupedManagerOptions = (managers = [], { disableDeactivated = false } = {}) => {
    const activeManagers = managers.filter(manager => manager.is_active);
    const deactivatedManagers = managers.filter(manager => !manager.is_active);

    const renderManagerOption = (manager) => (
        <Select.Option
            key={manager.id}
            value={manager.id}
            disabled={disableDeactivated && !manager.is_active}
        >
            {manager.name}
            {!manager.is_active && ' (Deactivated)'}
        </Select.Option>
    );

    return (
        <>
            {activeManagers.length > 0 && (
                <Select.OptGroup label="Active user">
                    {activeManagers.map(renderManagerOption)}
                </Select.OptGroup>
            )}
            {deactivatedManagers.length > 0 && (
                <Select.OptGroup label="Deactivated user">
                    {deactivatedManagers.map(renderManagerOption)}
                </Select.OptGroup>
            )}
        </>
    );
};

const formatPreviewDateTime = (value) => {
    if (!value) return '-';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : String(value);
};

const getExitFeedbackStatusColor = (status) => {
    if (status === 'sent' || status === 'responded') return 'green';
    if (status === 'scheduled' || status === 'due') return 'gold';
    if (status?.startsWith('missing')) return 'red';
    return 'default';
};

const getExitFeedbackAlertType = (status) => {
    if (status === 'sent' || status === 'responded') return 'success';
    if (status === 'scheduled' || status === 'due') return 'info';
    if (status?.startsWith('missing')) return 'warning';
    return 'info';
};

const getExitFeedbackTestEmailMessage = (result) => {
    const text = result?.message || result?.error || '';
    if (text.includes('Mailgun is not configured')) {
        return 'No test email was sent because this local HRP copy is not connected to the email sending service. Use the questionnaire preview link above to see what the former employee will see.';
    }
    if (text.includes('Email skipped in non-production environment')) {
        return 'No test email was sent because this local HRP copy is set to preview emails only. Use the questionnaire preview link above to see what the former employee will see.';
    }
    return text || `Test email was prepared for ${EXIT_FEEDBACK_TEST_EMAIL}, but local email sending is not configured.`;
};

const UserEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [user, setUser] = useState(null);
    const [dropdowns, setDropdowns] = useState({
        offices: [],
        departments: [],
        managers: [],
        roles: [],
        userRoles: [],
        recruitApplicants: [],
        checklists: []
    });
    const [fileList, setFileList] = useState([]);
    const [showPip, setShowPip] = useState(false);
    const [showTermination, setShowTermination] = useState(false);
    const [syncingManagerStatus, setSyncingManagerStatus] = useState(false);
    const [sendingExitFeedbackTestEmail, setSendingExitFeedbackTestEmail] = useState(false);

    // Cost Rate Modal States
    const [costRateModalVisible, setCostRateModalVisible] = useState(false);
    const [editingCostRate, setEditingCostRate] = useState(null);
    const [costRateForm] = Form.useForm();

    // Salary Modal States
    const [salaryModalVisible, setSalaryModalVisible] = useState(false);
    const [editingSalary, setEditingSalary] = useState(null);
    const [salaryForm] = Form.useForm();

    // Title History Modal States
    const [titleModalVisible, setTitleModalVisible] = useState(false);
    const [editingTitle, setEditingTitle] = useState(null);
    const [titleForm] = Form.useForm();
    const isManagerScopedOnly = roleService.isManager() && !roleService.hasAnyRole([
        ROLES.ROLE_HR,
        ROLES.ROLE_FINANCE,
        ROLES.ROLE_MANAGEMENT,
        ROLES.ROLE_OWNER
    ]);
    const canManagePrivilegeFields = !isManagerScopedOnly;
    const canSyncManagerAccess = roleService.canManageHR();
    const canCompleteJoiningAccount = roleService.hasAnyRole([ROLES.ROLE_HR, ROLES.ROLE_MANAGEMENT, ROLES.ROLE_OWNER]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadUserAndDropdowns();
    }, [id]);

    const loadUserAndDropdowns = async () => {
        setLoadingData(true);
        try {
            const [userData, dropdownData] = await Promise.all([
                userService.getUser(id),
                userService.getDropdowns()
            ]);

            setUser(userData);
            setDropdowns(dropdownData);

            // Check if PIP or Termination sections should be shown
            const hasPip = userData.pip_date || userData.pip_end_date || userData.pip_extension_date || userData.pip_file;
            const hasTermination = userData.job_terminate_date;
            setShowPip(!!hasPip);
            setShowTermination(!!hasTermination);

            const joinDate = userData.join_date ? dayjs(userData.join_date) : null;
            const jobExpiryDate = userData.job_expiry_date ? dayjs(userData.job_expiry_date) : null;
            const jobStatus = userData.job_status || 'Probation';
            const autoProbationEndDate = getAutoProbationEndDate({
                jobStatus,
                joinDate,
                jobExpiryDate,
            });

            // Populate form
            form.setFieldsValue({
                email: userData.email,
                first_name: userData.first_name,
                middle_name: userData.middle_name,
                last_name: userData.last_name,
                job_title: userData.job_title,
                office_id: userData.office?.id,
                public_holiday_id: userData.public_holiday_id,
                department_id: userData.department?.id,
                manager_id: userData.manager?.id,
                sub_manager_ids: userData.sub_managers?.map(sm => sm.id) || [],
                replace_manager_id: userData.replace_manager_id || userData.manager?.id,
                join_date: joinDate,
                job_permanent_date: userData.job_permanent_date ? dayjs(userData.job_permanent_date) : null,
                job_expiry_date: jobExpiryDate || autoProbationEndDate,
                job_terminate_date: userData.job_terminate_date ? dayjs(userData.job_terminate_date) : null,
                job_terminate_reason: normalizeToCode(userData.job_terminate_reason, 'job_terminate_reason'),
                send_exit_feedback_email: Boolean(userData.send_exit_feedback_email),
                freelancer: userData.freelancer || false,
                is_active: userData.is_active !== undefined ? userData.is_active : true,
                job_status: jobStatus,
                working_status: userData.working_status || 'status.working',
                job_description: userData.job_description,
                job_responsibility: userData.job_responsibility,
                roles: getEditableRoles(userData.roles, dropdownData.roles),
                all_office_access: userData.all_office_access || false,
                user_role_id: userData.user_role?.id,
                pip_date: userData.pip_date ? dayjs(userData.pip_date) : null,
                pip_end_date: userData.pip_end_date ? dayjs(userData.pip_end_date) : null,
                pip_extension_date: userData.pip_extension_date ? dayjs(userData.pip_extension_date) : null,
                recruit_applicant_id: userData.recruit_applicant?.id,
                onboarded_checklist_ids: userData.onboarded_checklists?.map(c => c.id) || [],
                permanent_checklist_ids: userData.permanent_checklists?.map(c => c.id) || [],
                offboarded_checklist_ids: userData.offboarded_checklists?.map(c => c.id) || [],
                annual_leave_adjustment: -1,
                fwa_status: userData.fwa_status || 'default',
                feature_document_management: userData.feature_document_management !== undefined ? userData.feature_document_management : true,
                // Vaccination
                vaccination_status: userData.vaccination?.status,
                vaccination_last_date: userData.vaccination?.last_vaccination_date ? dayjs(userData.vaccination.last_vaccination_date) : null,
                vaccination_effective_date: userData.vaccination?.effective_date ? dayjs(userData.vaccination.effective_date) : null
            });
        } catch (error) {
            message.error('Failed to load user data');
        } finally {
            setLoadingData(false);
        }
    };

    const handleRolesChange = (roles) => {
        if (requiresAllOfficeAccess(roles)) {
            form.setFieldsValue({ all_office_access: true });
        }
    };

    const fillMissingProbationEndDate = (nextValues = {}) => {
        const values = {
            jobStatus: form.getFieldValue('job_status'),
            joinDate: form.getFieldValue('join_date'),
            jobExpiryDate: form.getFieldValue('job_expiry_date'),
            ...nextValues,
        };
        const autoProbationEndDate = getAutoProbationEndDate(values);
        if (autoProbationEndDate) {
            form.setFieldsValue({ job_expiry_date: autoProbationEndDate });
        }
    };

    const handleJoinDateChange = (date) => {
        fillMissingProbationEndDate({ joinDate: date });
    };

    const handleJobStatusChange = (status) => {
        fillMissingProbationEndDate({ jobStatus: status });
    };

    const handleTerminationToggle = (event) => {
        const checked = event.target.checked;
        setShowTermination(checked);
        if (!checked) {
            form.setFieldsValue({ send_exit_feedback_email: false });
        }
    };

    const handleFileChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const payload = {
                ...values,
                roles: normalizeRoles(values.roles),
                join_date: values.join_date ? values.join_date.format('YYYY-MM-DD') : null,
                job_permanent_date: values.job_permanent_date ? values.job_permanent_date.format('YYYY-MM-DD') : null,
                job_expiry_date: values.job_expiry_date ? values.job_expiry_date.format('YYYY-MM-DD') : null,
                job_terminate_date: values.job_terminate_date ? values.job_terminate_date.format('YYYY-MM-DD') : null,
                pip_date: values.pip_date ? values.pip_date.format('YYYY-MM-DD') : null,
                pip_end_date: values.pip_end_date ? values.pip_end_date.format('YYYY-MM-DD') : null,
                pip_extension_date: values.pip_extension_date ? values.pip_extension_date.format('YYYY-MM-DD') : null
            };

            if (user?.status === 'status.joining' && !payload.email) delete payload.email;

            if (isManagerScopedOnly) {
                delete payload.roles;
                delete payload.all_office_access;
                delete payload.office_id;
                delete payload.public_holiday_id;
                delete payload.manager_id;
                delete payload.sub_manager_ids;
                delete payload.feature_document_management;
            }

            if (fileList.length > 0 && fileList[0].originFileObj) {
                const formData = new FormData();
                formData.append('pip_file', fileList[0].originFileObj);
                // TODO: Upload PIP file separately
            }

            await userService.updateUser(id, payload);
            message.success('User updated successfully');
            navigate('/admin/hr/employees');
        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else {
                message.error(getApiErrorMessage(error, 'Failed to update user'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSaveCompanyEmail = async () => {
        try {
            const { email } = await form.validateFields(['email']);
            if (!email) { message.error('Enter the company email.'); return; }
            setLoading(true);
            await userService.updateUser(id, { email });
            message.success('Company email saved. Staff access remains pending.');
            await loadUserAndDropdowns();
        } catch (error) {
            message.error(getApiErrorMessage(error, 'Could not save the company email.'));
        } finally { setLoading(false); }
    };

    const handleCancel = () => {
        navigate('/admin/hr/employees');
    };

    const handleSyncManagerStatus = async () => {
        setSyncingManagerStatus(true);
        try {
            await userService.syncManagerStatus(id);
            message.success('Manager access synced successfully');
            await loadUserAndDropdowns();
        } catch (error) {
            message.error(getApiErrorMessage(error, 'Failed to sync manager access'));
        } finally {
            setSyncingManagerStatus(false);
        }
    };

    const handleSendExitFeedbackTestEmail = async () => {
        setSendingExitFeedbackTestEmail(true);
        try {
            const result = await userService.sendExitFeedbackTestEmail(id);
            if (result?.sent) {
                message.success(`Test email sent to ${result.to || EXIT_FEEDBACK_TEST_EMAIL}`);
            } else if (result?.status === 'skipped') {
                message.warning(getExitFeedbackTestEmailMessage(result), 8);
            } else {
                message.error(result?.message || result?.error || 'Failed to send test email.');
            }
        } catch (error) {
            message.error(getApiErrorMessage(error, 'Failed to send test email.'));
        } finally {
            setSendingExitFeedbackTestEmail(false);
        }
    };

    const renderExitFeedbackEmailPreview = () => {
        const preview = user?.exit_feedback_email_preview;
        if (!preview) return null;
        const previewLinkParams = new URLSearchParams();
        if (preview.recipient_name) {
            previewLinkParams.set('name', preview.recipient_name);
        }
        const previewLinkQuery = previewLinkParams.toString();
        const questionnairePreviewLink = preview.preview_link
            || (preview.survey_id
                ? `${window.location.origin}/external-survey-preview/${encodeURIComponent(String(preview.survey_id))}${previewLinkQuery ? `?${previewLinkQuery}` : ''}`
                : null);

        const renderContentPreview = () => {
            const contentLines = Array.isArray(preview.content_preview) ? preview.content_preview : [];

            return contentLines.map((line, index) => {
                const text = String(line || '');
                const isQuestionnaireButton = text.startsWith('[Complete Feedback Questionnaire');

                if (isQuestionnaireButton) {
                    return (
                        <div key={`exit-feedback-content-${index}`} style={{ margin: '18px 0' }}>
                            {questionnairePreviewLink ? (
                                <a
                                    href={questionnairePreviewLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 40,
                                        padding: '10px 14px',
                                        background: '#ffd426',
                                        color: '#1f1f1f',
                                        borderRadius: 4,
                                        fontWeight: 700,
                                        textDecoration: 'none',
                                    }}
                                >
                                    Complete Feedback Questionnaire
                                </a>
                            ) : (
                                <strong>Complete Feedback Questionnaire</strong>
                            )}
                        </div>
                    );
                }

                return (
                    <p key={`exit-feedback-content-${index}`} style={{ margin: index === 0 ? '0 0 14px' : '14px 0' }}>
                        {text}
                    </p>
                );
            });
        };

        return (
            <div style={{ border: '1px solid #eeeeee', borderRadius: 6, padding: 16, marginBottom: 16, background: '#fafafa' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                    <strong>Exit Feedback email preview</strong>
                    <Tag color={getExitFeedbackStatusColor(preview.status)}>{preview.status_label}</Tag>
                </div>

                <Alert
                    type={getExitFeedbackAlertType(preview.status)}
                    showIcon
                    message={preview.status_message}
                    style={{ marginBottom: 12 }}
                />

                <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col xs={24} md={12}>
                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Subject</div>
                        <div>{preview.subject || '-'}</div>
                    </Col>
                    <Col xs={24} md={12}>
                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>To</div>
                        <div>{preview.to || 'Missing personal email'}</div>
                    </Col>
                    <Col xs={24} md={12}>
                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>BCC</div>
                        <div>{(preview.bcc || []).join(', ') || '-'}</div>
                    </Col>
                    <Col xs={24} md={12}>
                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>From</div>
                        <div>{preview.from || '-'}</div>
                    </Col>
                    <Col xs={24} md={12}>
                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Scheduled for</div>
                        <div>{formatPreviewDateTime(preview.scheduled_for)}</div>
                    </Col>
                    <Col xs={24} md={12}>
                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Sent at</div>
                        <div>{preview.sent_at ? formatPreviewDateTime(preview.sent_at) : 'Not sent yet'}</div>
                    </Col>
                    <Col xs={24} md={12}>
                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Submitted at</div>
                        <div>{preview.submitted_at ? formatPreviewDateTime(preview.submitted_at) : 'Not submitted yet'}</div>
                    </Col>
                    <Col xs={24} md={12}>
                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Survey</div>
                        <div>{preview.survey_title || '-'}</div>
                    </Col>
                    <Col xs={24}>
                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Questionnaire button</div>
                        <div>{preview.questionnaire_link_label || 'Unique questionnaire link generated when HRP sends the email'}</div>
                        {preview.questionnaire_link_note && (
                            <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
                                {preview.questionnaire_link_note}
                            </div>
                        )}
                    </Col>
                    <Col xs={24}>
                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>Questionnaire preview</div>
                        {questionnairePreviewLink ? (
                            <a
                                href={questionnairePreviewLink}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: '#1f1f1f', textDecoration: 'underline' }}
                            >
                                {preview.preview_link_label || 'Open questionnaire preview'}
                            </a>
                        ) : (
                            <div>Create and publish an Exit feedback survey first.</div>
                        )}
                    </Col>
                    <Col xs={24}>
                        <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 6 }}>Test email</div>
                        <Button
                            variant="secondary"
                            uppercase={false}
                            type="button"
                            loading={sendingExitFeedbackTestEmail}
                            disabled={!preview.survey_id}
                            onClick={handleSendExitFeedbackTestEmail}
                        >
                            Send test email to {EXIT_FEEDBACK_TEST_EMAIL}
                        </Button>
                    </Col>
                </Row>

                <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 6 }}>Content</div>
                <div style={{ background: '#ffffff', border: '1px solid #eeeeee', borderRadius: 4, padding: 12, lineHeight: 1.6 }}>
                    {renderContentPreview()}
                </div>
            </div>
        );
    };

    // ========================================================================
    // COST RATE HANDLERS
    // ========================================================================

    const handleAddCostRate = () => {
        setEditingCostRate(null);
        costRateForm.resetFields();
        setCostRateModalVisible(true);
    };

    const handleEditCostRate = (rate) => {
        setEditingCostRate(rate);
        costRateForm.setFieldsValue({
            rate: rate.rate,
            valid_at: rate.valid_at ? dayjs(rate.valid_at) : null
        });
        setCostRateModalVisible(true);
    };

    const handleDeleteCostRate = (rate) => {
        AntModal.confirm({
            title: 'Delete Cost Rate',
            content: `Are you sure you want to delete the cost rate from ${new Date(rate.valid_at).toLocaleDateString('en-GB')}?`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await userService.deleteCostRate(id, rate.id);
                    message.success('Cost rate deleted successfully');
                    loadUserAndDropdowns();
                } catch (error) {
                    message.error(getApiErrorMessage(error, 'Failed to delete cost rate'));
                }
            }
        });
    };

    const handleSaveCostRate = async () => {
        try {
            const values = await costRateForm.validateFields();
            setLoading(true);

            const payload = {
                rate: values.rate,
                valid_at: values.valid_at.format('YYYY-MM-DD')
            };

            if (editingCostRate) {
                await userService.updateCostRate(id, editingCostRate.id, payload);
                message.success('Cost rate updated successfully');
            } else {
                await userService.createCostRate(id, payload);
                message.success('Cost rate created successfully');
            }

            setCostRateModalVisible(false);
            loadUserAndDropdowns();
        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else {
                message.error(getApiErrorMessage(error, 'Failed to save cost rate'));
            }
        } finally {
            setLoading(false);
        }
    };

    // ========================================================================
    // SALARY HANDLERS
    // ========================================================================

    const handleAddSalary = () => {
        setEditingSalary(null);
        salaryForm.resetFields();
        setSalaryModalVisible(true);
    };

    const handleEditSalary = (salary) => {
        setEditingSalary(salary);
        salaryForm.setFieldsValue({
            amount: salary.amount,
            hourly_rate: salary.hourly_rate,
            valid_at: salary.valid_at ? dayjs(salary.valid_at) : null,
            office_id: salary.office?.id
        });
        setSalaryModalVisible(true);
    };

    const handleDeleteSalary = (salary) => {
        AntModal.confirm({
            title: 'Delete Salary',
            content: `Are you sure you want to delete the salary record from ${new Date(salary.valid_at).toLocaleDateString('en-GB')}?`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await userService.deleteSalary(id, salary.id);
                    message.success('Salary deleted successfully');
                    loadUserAndDropdowns();
                } catch (error) {
                    message.error(getApiErrorMessage(error, 'Failed to delete salary'));
                }
            }
        });
    };

    const handleSaveSalary = async () => {
        try {
            const values = await salaryForm.validateFields();
            setLoading(true);

            const payload = {
                amount: values.amount,
                hourly_rate: values.hourly_rate,
                valid_at: values.valid_at.format('YYYY-MM-DD'),
                office_id: values.office_id || null
            };

            if (editingSalary) {
                await userService.updateSalary(id, editingSalary.id, payload);
                message.success('Salary updated successfully');
            } else {
                await userService.createSalary(id, payload);
                message.success('Salary created successfully');
            }

            setSalaryModalVisible(false);
            loadUserAndDropdowns();
        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else {
                message.error(getApiErrorMessage(error, 'Failed to save salary'));
            }
        } finally {
            setLoading(false);
        }
    };

    // ========================================================================
    // TITLE HISTORY HANDLERS
    // ========================================================================

    const handleAddTitle = () => {
        setEditingTitle(null);
        titleForm.resetFields();
        setTitleModalVisible(true);
    };

    const handleEditTitle = (title) => {
        setEditingTitle(title);
        titleForm.setFieldsValue({
            title: title.title,
            valid_at: title.valid_at ? dayjs(title.valid_at) : null
        });
        setTitleModalVisible(true);
    };

    const handleDeleteTitle = (title) => {
        AntModal.confirm({
            title: 'Delete Title',
            content: `Are you sure you want to delete the title "${title.title}"?`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await userService.deleteTitle(id, title.id);
                    message.success('Title deleted successfully');
                    loadUserAndDropdowns();
                } catch (error) {
                    message.error(getApiErrorMessage(error, 'Failed to delete title'));
                }
            }
        });
    };

    const handleSaveTitle = async () => {
        try {
            const values = await titleForm.validateFields();
            setLoading(true);

            const payload = {
                title: values.title,
                valid_at: values.valid_at.format('YYYY-MM-DD')
            };

            if (editingTitle) {
                await userService.updateTitle(id, editingTitle.id, payload);
                message.success('Title updated successfully');
            } else {
                await userService.createTitle(id, payload);
                message.success('Title created successfully');
            }

            setTitleModalVisible(false);
            loadUserAndDropdowns();
        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else {
                message.error(getApiErrorMessage(error, 'Failed to save title'));
            }
        } finally {
            setLoading(false);
        }
    };

    // React hooks must be called before any conditional returns
    const jobStatus = Form.useWatch('job_status', form);
    const isProbation = requiresProbationEndDate(jobStatus);
    const isProbationOrLimited = ['Probation', 'Time-Limited', 'Temporary'].includes(jobStatus);

    if (loadingData) {
        return (
            <div className="wrapper-grey finance-container">
                <Card>
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>Loading user data...</p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="wrapper-grey finance-container">
            <Card>
                <div
                    className="section-header"
                    style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', gap: 12 }}
                >
                    <div className="header-left" style={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center' }}>
                        <h4>
                            <ArrowLeftOutlined
                                onClick={handleCancel}
                                style={{ marginRight: 12, cursor: 'pointer' }}
                            />
                            EDIT USER
                        </h4>
                    </div>
                    <div className="header-right" style={{ marginLeft: 'auto', flexShrink: 0, width: 'auto', justifyContent: 'flex-end' }}>
                        <Button
                            variant="secondary"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/profile/${id}`)}
                        >
                            Edit Profile
                        </Button>
                    </div>
                </div>

                {/* User Header with Profile Picture */}
                <Row style={{ marginBottom: 24, marginTop: 16 }}>
                    <Col span={24}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Avatar
                                size={75}
                                src={user?.profile_picture}
                                icon={!user?.profile_picture && <UserOutlined />}
                                style={{ border: '3px solid #52c41a' }}
                            />
                            <h2 style={{ margin: 0 }}>{user?.full_name}</h2>
                        </div>
                    </Col>
                </Row>

                {canSyncManagerAccess && user?.manager_access_drift && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 24 }}
                        message="Manager access is out of sync"
                        description={`${user.full_name} has ${user.active_direct_reports_count} active direct report${user.active_direct_reports_count === 1 ? '' : 's'}, but manager access is not enabled.`}
                        action={(
                            <Button
                                variant="secondary"
                                loading={syncingManagerStatus}
                                onClick={handleSyncManagerStatus}
                            >
                                Sync Manager Access
                            </Button>
                        )}
                    />
                )}

                {user?.status === 'status.joining' && <Alert type="info" showIcon style={{ marginBottom: 24 }}
                    message={user.email ? 'Staff access pending' : 'Company email pending'}
                    description="The contract is signed and the employee profile is ready. HR can save the company email below, then complete the remaining setup before enabling access." />}
                <Form form={form} layout="vertical">
                    <Row gutter={24}>
                        {/* COLUMN 1 */}
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Company email"
                                name="email"
                                help={user?.status === 'status.joining' ? 'Contract signed. Add the company email, then enable staff access when ready.' : undefined}
                                rules={[{ required: user?.status !== 'status.joining', type: 'email' }]}
                            >
                                <Input disabled={user?.status !== 'status.joining' || !canCompleteJoiningAccount} autoComplete="off" placeholder="Company email pending" />
                            </Form.Item>
                            {user?.status === 'status.joining' && canCompleteJoiningAccount && <Button variant="secondary" loading={loading} onClick={handleSaveCompanyEmail} style={{ marginBottom: 24 }}>Save company email</Button>}

                            <div style={{ display: 'flex', gap: 8, marginBottom: 24, alignItems: 'flex-end' }}>
                                <Form.Item
                                    label="Roles"
                                    name="roles"
                                    rules={[{ type: 'array', min: 1, required: true, message: 'Select at least one role' }]}
                                    style={{ flex: 1, marginBottom: 0 }}
                                >
                                    <Select
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        placeholder="Select roles"
                                        onChange={handleRolesChange}
                                        disabled={!canManagePrivilegeFields}
                                    >
                                        {dropdowns.roles?.map(role => (
                                            <Select.Option key={role.key} value={role.key}>
                                                {role.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    name="all_office_access"
                                    valuePropName="checked"
                                    style={{ marginBottom: 4 }}
                                >
                                    <Checkbox disabled={!canManagePrivilegeFields}>All Office</Checkbox>
                                </Form.Item>
                            </div>

                            <Form.Item label="Account Status" name="is_active" rules={[{ required: true }]}>
                                <Select disabled={user?.status === 'status.joining' && !canCompleteJoiningAccount}>
                                    <Select.Option value={true}>Active</Select.Option>
                                    <Select.Option value={false}>{user?.status === 'status.joining' ? 'Access pending' : 'Deactivated'}</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="Working Status" name="working_status">
                                <Select>
                                    <Select.Option value="status.working">Working</Select.Option>
                                    <Select.Option value="status.working_home">Working From Home</Select.Option>
                                    <Select.Option value="status.sick">Sick</Select.Option>
                                    <Select.Option value="status.leave">On Leave</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="FWA Status" name="fwa_status" help="Can only be changed if no FWA has been requested or activated">
                                <Select>
                                    <Select.Option value="default">User Default</Select.Option>
                                    <Select.Option value="page.user.profile.fwa.option.fwh">From Home</Select.Option>
                                    <Select.Option value="page.user.profile.fwa.option.fp">Flexiplace</Select.Option>
                                    <Select.Option value="page.user.profile.fwa.option.pt">Part Time</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="Link with Candidate from Recruitment" name="recruit_applicant_id">
                                <Select placeholder="-- Select --" allowClear showSearch>
                                    {dropdowns.recruitApplicants?.map(applicant => (
                                        <Select.Option key={applicant.id} value={applicant.id}>
                                            {applicant.id} - {applicant.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* COLUMN 2 */}
                        <Col xs={24} md={8}>
                            <Form.Item label="Job/User Title" name="job_title" rules={[{ required: true }]}>
                                <Input autoComplete="off" />
                            </Form.Item>

                            <Form.Item label="Billable Rate Card" name="user_role_id">
                                <Select placeholder="-- Select --" allowClear showSearch>
                                    {dropdowns.userRoles?.map(role => (
                                        <Select.Option key={role.id} value={role.id}>
                                            {role.department} - {role.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Office" name="office_id" rules={[{ required: true }]}>
                                <Select showSearch disabled={!canManagePrivilegeFields}>
                                    {dropdowns.offices?.map(office => (
                                        <Select.Option key={office.id} value={office.id}>
                                            {office.full_name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Public Holiday Calendar" name="public_holiday_id" help="Only change if you want a different public holiday calendar">
                                <Select placeholder="Office Default" allowClear showSearch disabled={!canManagePrivilegeFields}>
                                    {dropdowns.offices?.map(office => (
                                        <Select.Option key={office.id} value={office.id}>
                                            {office.full_name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Is Freelancer" name="freelancer">
                                <Select>
                                    <Select.Option value={false}>No</Select.Option>
                                    <Select.Option value={true}>Yes</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="Primary Department" name="department_id" rules={[{ required: true }]}>
                                <Select showSearch>
                                    {dropdowns.departments?.map(dept => (
                                        <Select.Option key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Manager" name="manager_id">
                                <Select placeholder="No manager" allowClear showSearch disabled={!canManagePrivilegeFields}>
                                    {renderGroupedManagerOptions(dropdowns.managers)}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Sub Managers" name="sub_manager_ids">
                                <Select mode="multiple" placeholder="-- Select --" showSearch disabled={!canManagePrivilegeFields}>
                                    {renderGroupedManagerOptions(dropdowns.managers, { disableDeactivated: true })}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Annual Leave"
                                name="annual_leave_adjustment"
                                style={{ display: 'none' }}
                            >
                                <InputNumber style={{ width: '100%' }} disabled />
                            </Form.Item>
                        </Col>

                        {/* COLUMN 3 */}
                        <Col xs={24} md={8}>
                            <Form.Item label="Join Date" name="join_date" rules={[{ required: true }]}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    onChange={handleJoinDateChange}
                                />
                            </Form.Item>

                            <Form.Item label="Employment Status" name="job_status">
                                <Select onChange={handleJobStatusChange}>
                                    <Select.Option value="Probation">Probation</Select.Option>
                                    <Select.Option value="Permanent">Permanent</Select.Option>
                                    <Select.Option value="Time-Limited">Time-Limited</Select.Option>
                                    <Select.Option value="Temporary">Temporary</Select.Option>
                                    <Select.Option value="Exiting">Exiting</Select.Option>
                                    <Select.Option value="Ended">Ended</Select.Option>
                                </Select>
                            </Form.Item>

                            {/* Conditional Checklists based on Employment Status */}
                            {isProbationOrLimited ? (
                                <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, marginBottom: 16 }}>
                                    <Form.Item label="Onboarding Checklists" name="onboarded_checklist_ids" style={{ marginBottom: 0 }}>
                                        <Select mode="multiple" placeholder="-- Select Checklists --" showSearch>
                                            {dropdowns.checklists?.filter(c => c.type === 'ONBOARDING').map(checklist => (
                                                <Select.Option key={checklist.id} value={checklist.id}>
                                                    {checklist.name}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </div>
                            ) : (
                                <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, marginBottom: 16 }}>
                                    <Form.Item label="Permanent Checklist" name="permanent_checklist_ids" style={{ marginBottom: 0 }}>
                                        <Select mode="multiple" placeholder="-- Select Checklists --" showSearch>
                                            {dropdowns.checklists?.filter(c => c.type === 'PERMANENT').map(checklist => (
                                                <Select.Option key={checklist.id} value={checklist.id}>
                                                    {checklist.name}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </div>
                            )}

                            <Row gutter={8}>
                                {!isProbationOrLimited && (
                                    <Col span={12}>
                                        <Form.Item
                                            label="Permanent Date"
                                            name="job_permanent_date"
                                            rules={[{ required: normalizeToCode(jobStatus, 'job_status') === 'permanent', message: 'Please select permanent date' }]}
                                        >
                                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                        </Form.Item>
                                    </Col>
                                )}
                                <Col span={isProbationOrLimited ? 24 : 12}>
                                    <Form.Item
                                        label={isProbationOrLimited ? "Probation End Date" : "Next Review Date"}
                                        name="job_expiry_date"
                                        rules={isProbation ? [{ required: true, message: 'Please select probation end date' }] : []}
                                    >
                                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* PIP Section */}
                            <div style={{ borderTop: '1px solid #d9d9d9', paddingTop: 16, marginTop: 8 }}>
                                <div style={{ marginBottom: 8 }}>
                                    <Checkbox
                                        checked={showPip}
                                        onChange={(e) => setShowPip(e.target.checked)}
                                        style={{ fontWeight: 600 }}
                                    >
                                        Performance Improvement Plan (PIP)
                                    </Checkbox>
                                </div>

                                {showPip && (
                                    <div style={{ marginTop: 16 }}>
                                        <Form.Item label="PIP Document" help="Max size 10MB. Supported files: .jpg, .jpeg, .png, .gif, .webp, .pdf">
                                            <Upload
                                                fileList={fileList}
                                                onChange={handleFileChange}
                                                beforeUpload={() => false}
                                                maxCount={1}
                                                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                                            >
                                                <Button icon={<UploadOutlined />}>Choose File...</Button>
                                            </Upload>
                                        </Form.Item>

                                        <Row gutter={8}>
                                            <Col span={12}>
                                                <Form.Item label="PIP Review Date" name="pip_date">
                                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="PIP End Date" name="pip_end_date">
                                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Form.Item label="PIP Extension Date (optional)" name="pip_extension_date">
                                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                        </Form.Item>
                                    </div>
                                )}
                            </div>

                            {/* Termination Section */}
                            <div style={{ borderTop: '1px solid #d9d9d9', paddingTop: 16, marginTop: 16 }}>
                                <div style={{ marginBottom: 8 }}>
                                    <Checkbox
                                        checked={showTermination}
                                        onChange={handleTerminationToggle}
                                        style={{ fontWeight: 600 }}
                                    >
                                        Schedule Termination
                                    </Checkbox>
                                </div>

                                {showTermination && (
                                    <div style={{ marginTop: 16 }}>
                                        <Form.Item name="send_exit_feedback_email" valuePropName="checked" style={{ marginBottom: 16 }}>
                                            <Checkbox>Send Exit Feedback email?</Checkbox>
                                        </Form.Item>

                                        {renderExitFeedbackEmailPreview()}

                                        <Form.Item label="Termination Reason" name="job_terminate_reason">
                                            <Select placeholder="-- Select --" allowClear>
                                                <Select.Option value="resign">Resign</Select.Option>
                                                <Select.Option value="end_of_contract">End of Contract</Select.Option>
                                                <Select.Option value="end_of_internship">End of Internship</Select.Option>
                                                <Select.Option value="probation_failed">Probation Failed</Select.Option>
                                                <Select.Option value="terminate">Terminate</Select.Option>
                                                <Select.Option value="prolonged_illness">Prolonged Illness</Select.Option>
                                            </Select>
                                        </Form.Item>

                                        <Form.Item label="Last Day" name="job_terminate_date">
                                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                        </Form.Item>

                                        <Form.Item
                                            label="Transfer Responsibilities To"
                                            name="replace_manager_id"
                                            extra="Required when this employee manages active direct reports or active projects."
                                        >
                                            <Select placeholder="-- Select --" allowClear showSearch>
                                                {renderGroupedManagerOptions(dropdowns.managers, { disableDeactivated: true })}
                                            </Select>
                                        </Form.Item>

                                        <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4 }}>
                                            <Form.Item label="Offboarding Checklist" name="offboarded_checklist_ids" style={{ marginBottom: 0 }}>
                                                <Select mode="multiple" placeholder="-- Select Checklists --" showSearch>
                                                    {dropdowns.checklists?.filter(c => c.type === 'OFFBOARDING').map(checklist => (
                                                        <Select.Option key={checklist.id} value={checklist.id}>
                                                            {checklist.name}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>

                    {/* Exit Impact Score (Read-only) */}
                    {user?.exit_impact_score?.totalScore != null && (() => {
                        const eis = user.exit_impact_score;
                        const perfPoints = { high: 3, average: 2, low: 1 }[eis.performance] || 0;
                        const easePoints = { easy: 0, medium: 1, difficult: 2 }[eis.replacementEase] || 0;
                        const scoreColor = eis.totalScore <= 3 ? '#52c41a' : eis.totalScore <= 5 ? '#faad14' : '#f5222d';
                        const scoreLabel = eis.totalScore <= 3 ? 'Low Impact' : eis.totalScore <= 5 ? 'Medium Impact' : 'High Impact';
                        const filledDate = eis.filledAt ? new Date(eis.filledAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : null;
                        return (
                        <Card
                            size="small"
                            style={{ marginTop: 24, border: `1px solid ${scoreColor}30` }}
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Exit Impact Score</span>
                                    <Tag color={scoreColor} style={{ fontSize: 13, marginRight: 0 }}>{eis.totalScore} / 7 — {scoreLabel}</Tag>
                                </div>
                            }
                        >
                            <Row gutter={[24, 16]}>
                                <Col xs={24} md={8}>
                                    <span style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>Would you rehire?</span>
                                    <span style={{ fontWeight: 500 }}>{eis.rehire ? 'Yes (+2)' : 'No (0)'}</span>
                                </Col>
                                <Col xs={24} md={8}>
                                    <span style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>Performance level</span>
                                    <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{eis.performance} (+{perfPoints})</span>
                                </Col>
                                <Col xs={24} md={8}>
                                    <span style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>Replacement ease</span>
                                    <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{eis.replacementEase} (+{easePoints})</span>
                                </Col>
                            </Row>
                            {eis.reasonLeaving && (
                                <div style={{ marginTop: 16 }}>
                                    <span style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>Reason for leaving</span>
                                    <div style={{ background: '#fafafa', padding: '8px 12px', borderRadius: 4 }}>{eis.reasonLeaving}</div>
                                </div>
                            )}
                            {filledDate && (
                                <div style={{ marginTop: 12, color: '#999', fontSize: 12 }}>
                                    Filled by {eis.filledByName || `user #${eis.filledBy}`} on {filledDate}
                                </div>
                            )}
                        </Card>
                        );
                    })()}

                    {/* Separator */}
                    <Row style={{ marginTop: 24 }}>
                        <Col span={24}>
                            <div style={{ borderTop: '1px solid #d9d9d9' }} />
                        </Col>
                    </Row>

                    {/* Existing Checklists Display */}
                    <Row gutter={24} style={{ marginTop: 24 }}>
                        {['ONBOARDING', 'PERMANENT', 'OFFBOARDING'].map(type => {
                            const checklists = user?.[`${type.toLowerCase()}_checklists`] || [];
                            if (checklists.length === 0) return null;

                            return (
                                <Col xs={24} md={8} key={type}>
                                    <div style={{ fontWeight: 600, marginBottom: 8 }}>
                                        {type.charAt(0) + type.slice(1).toLowerCase()} Checklists
                                    </div>
                                    <ul style={{ paddingLeft: 20 }}>
                                        {checklists.map(checklist => (
                                            <li key={checklist.id}>
                                                <a href={`/checklist/${checklist.id}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>
                                                    {checklist.name}
                                                </a>
                                                {checklist.progress && ` (${checklist.progress})`}
                                            </li>
                                        ))}
                                    </ul>
                                </Col>
                            );
                        })}
                        {!user?.onboarding_checklists?.length && !user?.permanent_checklists?.length && !user?.offboarded_checklists?.length && (
                            <Col span={24}>
                                <div style={{ textAlign: 'center', fontWeight: 600 }}>No checklists</div>
                            </Col>
                        )}
                    </Row>

                    {/* Separator */}
                    <Row style={{ marginTop: 24 }}>
                        <Col span={24}>
                            <div style={{ borderTop: '1px solid #d9d9d9' }} />
                        </Col>
                    </Row>

                    {/* Job Description & Responsibility */}
                    <Row gutter={24} style={{ marginTop: 24 }}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Job Description" name="job_description">
                                <TextArea rows={6} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Job Responsibility"
                                name="job_responsibility"
                                help="Each new line will be transformed into a list item"
                            >
                                <TextArea rows={6} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Separator */}
                    <Row style={{ marginTop: 24 }}>
                        <Col span={24}>
                            <div style={{ borderTop: '1px solid #d9d9d9' }} />
                        </Col>
                    </Row>

                    {/* Title History */}
                    <Row style={{ marginTop: 24 }}>
                        <Col span={24}>
                            <div style={{ marginBottom: 24 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                    <h5 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Title History</h5>
                                    <Button size="small" onClick={handleAddTitle}>Add Title</Button>
                                </div>
                                <table className="table table-sm" style={{ width: '100%', border: '1px solid #d9d9d9' }}>
                                    <thead>
                                        <tr style={{ background: '#fafafa' }}>
                                            <th style={{ padding: '8px', width: '40%', textAlign: 'left' }}>Title</th>
                                            <th style={{ padding: '8px', width: '25%', textAlign: 'left' }}>Valid From</th>
                                            <th style={{ padding: '8px', width: '25%', textAlign: 'left' }}>Created By</th>
                                            <th style={{ padding: '8px', width: '10%', textAlign: 'center' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {user?.title_history?.length > 0 ? (
                                            user.title_history.map((title, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ padding: '8px', fontWeight: idx === 0 ? 600 : 400 }}>
                                                        {title.title}
                                                        {idx === 0 && <span style={{ marginLeft: 8, fontSize: '11px', color: '#52c41a' }}>(Current)</span>}
                                                    </td>
                                                    <td style={{ padding: '8px' }}>{title.valid_at ? new Date(title.valid_at).toLocaleDateString('en-GB') : '-'}</td>
                                                    <td style={{ padding: '8px' }}>{title.created_by?.full_name || '-'}</td>
                                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                                        <ActionMenu
                                                            items={['edit', 'delete']}
                                                            record={title}
                                                            onAction={(key, record) => {
                                                                if (key === 'edit') handleEditTitle(record);
                                                                else if (key === 'delete') handleDeleteTitle(record);
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
                                                    No title history
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Col>
                    </Row>

                    {/* Cost Rate, Salary, Vaccination, Features */}
                    <Row gutter={24} style={{ marginTop: 24 }}>
                        {/* Column 1: Cost Rate & Salary */}
                        <Col xs={24} md={14}>
                            {/* Cost Rate Table */}
                            <div style={{ marginBottom: 24 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                    <h5 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Cost Rate</h5>
                                    <Button size="small" onClick={handleAddCostRate}>Add Rate</Button>
                                </div>
                                <table className="table table-sm" style={{ width: '100%', border: '1px solid #d9d9d9' }}>
                                    <thead>
                                        <tr style={{ background: '#fafafa' }}>
                                            <th style={{ padding: '8px', width: '30%', textAlign: 'left' }}>Valid At</th>
                                            <th style={{ padding: '8px', width: '30%', textAlign: 'left' }}>Monthly</th>
                                            <th style={{ padding: '8px', width: '30%', textAlign: 'left' }}>Hourly</th>
                                            <th style={{ padding: '8px', width: '10%', textAlign: 'center' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {user?.cost_rates?.length > 0 ? (
                                            user.cost_rates.map((rate, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ padding: '8px' }}>{rate.valid_at ? new Date(rate.valid_at).toLocaleDateString('en-GB') : '-'}</td>
                                                    <td style={{ padding: '8px' }}>{rate.rate || '-'}</td>
                                                    <td style={{ padding: '8px' }}>{rate.rate ? (rate.rate / 160).toFixed(2) : '-'}</td>
                                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                                        <ActionMenu
                                                            items={['edit', 'delete']}
                                                            record={rate}
                                                            onAction={(key, record) => {
                                                                if (key === 'edit') {
                                                                    handleEditCostRate(record);
                                                                } else if (key === 'delete') {
                                                                    handleDeleteCostRate(record);
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
                                                    No cost rates
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Salary Table - ROLE_FINANCE only */}
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                    <h5 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Salary</h5>
                                    <Button size="small" onClick={handleAddSalary}>Add Salary</Button>
                                </div>
                                <table className="table table-sm" style={{ width: '100%', border: '1px solid #d9d9d9' }}>
                                    <thead>
                                        <tr style={{ background: '#fafafa' }}>
                                            <th style={{ padding: '8px', width: '25%', textAlign: 'left' }}>Valid At</th>
                                            <th style={{ padding: '8px', width: '25%', textAlign: 'left' }}>Monthly</th>
                                            <th style={{ padding: '8px', width: '20%', textAlign: 'left' }}>Hourly</th>
                                            <th style={{ padding: '8px', width: '20%', textAlign: 'left' }}>Contract</th>
                                            <th style={{ padding: '8px', width: '10%', textAlign: 'center' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {user?.salaries?.length > 0 ? (
                                            user.salaries.map((salary, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ padding: '8px' }}>{salary.valid_at ? new Date(salary.valid_at).toLocaleDateString('en-GB') : '-'}</td>
                                                    <td style={{ padding: '8px' }}>{salary.amount || '-'}</td>
                                                    <td style={{ padding: '8px' }}>{salary.hourly_rate || '-'}</td>
                                                    <td style={{ padding: '8px' }}>{salary.office?.full_name || '-'}</td>
                                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                                        <ActionMenu
                                                            items={['edit', 'delete']}
                                                            record={salary}
                                                            onAction={(key, record) => {
                                                                if (key === 'edit') {
                                                                    handleEditSalary(record);
                                                                } else if (key === 'delete') {
                                                                    handleDeleteSalary(record);
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
                                                    No salaries
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Col>

                        {/* Column 2: Covid-19 Vaccination */}
                        <Col xs={24} md={5}>
                            <h5 style={{ marginBottom: 16, fontSize: 14, fontWeight: 600 }}>Covid-19 Vaccination</h5>
                            <Form.Item label="Status" name="vaccination_status">
                                <Select placeholder="-- Select one --" allowClear>
                                    <Select.Option value="not_vaccinated">Not Vaccinated</Select.Option>
                                    <Select.Option value="first_dose">First Dose</Select.Option>
                                    <Select.Option value="second_dose">Second Dose (Fully Vaccinated)</Select.Option>
                                    <Select.Option value="booster">Booster</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item label="Last Vaccination Date" name="vaccination_last_date">
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                            <Form.Item label="Effective Date" name="vaccination_effective_date" help="Second dose date + 14 days">
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>

                        {/* Column 3: Feature Access */}
                        <Col xs={24} md={5}>
                            <h5 style={{ marginBottom: 16, fontSize: 14, fontWeight: 600 }}>Feature Access</h5>
                            <Form.Item label="Edit Document Management" name="feature_document_management">
                                <Select disabled={!canManagePrivilegeFields}>
                                    <Select.Option value={true}>Enabled</Select.Option>
                                    <Select.Option value={false}>Disabled</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>

                    </Row>

                    {/* Action Buttons */}
                    <Row style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #d9d9d9' }}>
                        <Col span={24} style={{ textAlign: 'right' }}>
                            <Button
                                variant="secondary"
                                onClick={handleCancel}
                                style={{ marginRight: 8 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                loading={loading}
                            >
                                Save
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* Cost Rate Modal */}
            <Modal
                visible={costRateModalVisible}
                onClose={() => setCostRateModalVisible(false)}
                width="small"
            >
                <Modal.Header title={editingCostRate ? 'Edit Cost Rate' : 'Add Cost Rate'} />
                <Modal.Body padding="default">
                    <Form form={costRateForm} layout="vertical">
                        <Form.Item
                            label="Monthly Cost Rate"
                            name="rate"
                            rules={[
                                { required: true, message: 'Please enter monthly cost rate' },
                                { type: 'number', min: 0, message: 'Rate must be a positive number' }
                            ]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="Enter monthly cost rate"
                                min={0}
                                precision={2}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Valid From"
                            name="valid_at"
                            rules={[{ required: true, message: 'Please select valid from date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                        </Form.Item>

                        <div style={{ fontSize: '12px', color: '#666', marginTop: '-8px' }}>
                            Hourly rate will be calculated automatically (Monthly / 160)
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer
                    buttons={[
                        {
                            ...BUTTON_PRESETS.CANCEL,
                            key: 'cancel',
                            label: 'Cancel',
                            onClick: () => setCostRateModalVisible(false)
                        },
                        {
                            ...BUTTON_PRESETS.SAVE,
                            key: 'save',
                            label: 'Save',
                            onClick: handleSaveCostRate,
                            loading: loading
                        }
                    ]}
                />
            </Modal>

            {/* Salary Modal */}
            <Modal
                visible={salaryModalVisible}
                onClose={() => setSalaryModalVisible(false)}
                width="small"
            >
                <Modal.Header title={editingSalary ? 'Edit Salary' : 'Add Salary'} />
                <Modal.Body padding="default">
                    <Form form={salaryForm} layout="vertical">
                        <Form.Item
                            label="Monthly Salary"
                            name="amount"
                            rules={[
                                { required: true, message: 'Please enter monthly salary' },
                                { type: 'number', min: 0, message: 'Amount must be a positive number' }
                            ]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="Enter monthly salary"
                                min={0}
                                precision={2}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Hourly Rate"
                            name="hourly_rate"
                            rules={[
                                { required: true, message: 'Please enter hourly rate' },
                                { type: 'number', min: 0, message: 'Hourly rate must be a positive number' }
                            ]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="Enter hourly rate"
                                min={0}
                                precision={2}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Valid From"
                            name="valid_at"
                            rules={[{ required: true, message: 'Please select valid from date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                        </Form.Item>

                        <Form.Item
                            label="Contract/Office"
                            name="office_id"
                            help="Optional: Link to specific office contract"
                        >
                            <Select placeholder="-- Select Office --" allowClear showSearch>
                                {dropdowns.offices?.map(office => (
                                    <Select.Option key={office.id} value={office.id}>
                                        {office.full_name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </Modal.Body>
                <Modal.Footer
                    buttons={[
                        {
                            ...BUTTON_PRESETS.CANCEL,
                            key: 'cancel',
                            label: 'Cancel',
                            onClick: () => setSalaryModalVisible(false)
                        },
                        {
                            ...BUTTON_PRESETS.SAVE,
                            key: 'save',
                            label: 'Save',
                            onClick: handleSaveSalary,
                            loading: loading
                        }
                    ]}
                />
            </Modal>

            {/* Title History Modal */}
            <Modal
                visible={titleModalVisible}
                onClose={() => setTitleModalVisible(false)}
                width="small"
            >
                <Modal.Header title={editingTitle ? 'Edit Title' : 'Add Title'} />
                <Modal.Body padding="default">
                    <Form form={titleForm} layout="vertical">
                        <Form.Item
                            label="Job Title"
                            name="title"
                            rules={[{ required: true, message: 'Please enter job title' }]}
                        >
                            <Input placeholder="Enter job title" autoComplete="off" />
                        </Form.Item>

                        <Form.Item
                            label="Valid From"
                            name="valid_at"
                            rules={[{ required: true, message: 'Please select valid from date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                        </Form.Item>
                    </Form>
                </Modal.Body>
                <Modal.Footer
                    buttons={[
                        {
                            ...BUTTON_PRESETS.CANCEL,
                            key: 'cancel',
                            label: 'Cancel',
                            onClick: () => setTitleModalVisible(false)
                        },
                        {
                            ...BUTTON_PRESETS.SAVE,
                            key: 'save',
                            label: 'Save',
                            onClick: handleSaveTitle,
                            loading: loading
                        }
                    ]}
                />
            </Modal>
        </div>
    );
};

export default UserEdit;
